// Libraries
import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import {
  Button,
  Col,
  Dropdown,
  Form,
  FormGroup,
  OverlayTrigger,
  Row,
  Table,
  Tooltip,
} from "react-bootstrap";
import fileDownload from "js-file-download";

// Tools
import { validateUserRoleIds } from "../global/GlobalTools";

// Contexts
import { MainContext } from "../../App";

// Components
import ModalScoreFormat from "./modals/ModalScoreFormat";
import ModalImportScores from "./modals/ModalImportScores";

// API
import {
  CreateStudentComment,
  DeleteStudentClass,
  GetClassScoreCSV,
  GetClassScorePDF,
  GetPeriodsAndPartials,
  GetStudentClass,
  SaveStudentsScore,
} from "../../api/class";
import ModalConfirmDelete from "../global/modals/ModalConfirmDelete";

// Environment variables
const prefix = process.env.REACT_APP_PREFIX;

// Key for filters in localstorage
const filtersKey = `cmFiltersclassRating${prefix}`;

// Calculate container height based on window height
const screenHeight = window.innerHeight;
const containerHeight = screenHeight - 320;

// Custom styles
const customStyles = {
  iconStudent: {
    alignContent: "center",
    fontSize: "17px",
    color: "var(--red-primary)",
    cursor: "pointer",
  },

  iconRemoveX: {
    alignContent: "center",
    fontSize: "17px",
    color: "#b8bcbd",
    cursor: "pointer",
    marginTop: "-5px",
  },
};

const ClassGrades = ({ classId, ...props }) => {
  // Router
  const history = useHistory();
  // Contexts
  const { permissions } = useContext(MainContext);
  // i18n
  const { t, ready } = useTranslation(["class", "global", "captureGrades"]);
  // Main State
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingScores, setIsLoadingScores] = useState(false);
  const [classStudents, setClassStudents] = useState([]);
  const [classStudentsBackup, setClassStudentsBackup] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [periodSelected, setPeriodSelected] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [finalExamSelected, setFinalExamSelected] = useState(null);
  const [partialSelected, setPartialSelected] = useState(null);
  const [typeScore, setTypeScore] = useState("period");
  const [criteriaClass, setCriteriaClass] = useState([]);
  const [criteriaTab, setCriteriaTab] = useState(null);
  const [scoreConfig, setScoreConfig] = useState({});
  const [isDownloadingScores, setIsDownloadingScores] = useState(false);
  // Modals state
  const [showModalScoreFormat, setShowModalScoreFormat] = useState(false);
  const [showModalImportScores, setShowModalImportScores] = useState(false);

  const [showModalDelete, setShowModalDelete] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);

  useEffect(() => {
    if (ready) {
      getPeriodsForClass();
    }
  }, [ready]);

  useEffect(() => {
    if (props.refreshStudentList) {
      getPeriodsForClass();
      props.setRefreshStudentList(false);
    }
  }, [props.refreshStudentList]);

  const getPeriodsForClass = () => {
    const payload = {
      class_id: classId,
    };

    setIsLoading(true);

    GetPeriodsAndPartials(payload)
      .then((response) => {
        if (response.status) {
          const { assessments, grading_periods, score_config } = response.data;
          // Set score config
          setScoreConfig(score_config);
          // Format periods
          const periods = formatPeriods(grading_periods || []);
          setPeriods(periods);
          // Format assessments
          const formattedAssessments = formatAssessments(assessments || []);
          setAssessments(formattedAssessments);
          // Set preselected filters
          setPreselectedFilters(periods, formattedAssessments);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * Description: Function to format periods
   * @param {Array} [periods=[]]
   * @return {Array}
   */
  const formatPeriods = (periods = []) => {
    return periods.map((period, index) => {
      return {
        id: period.id,
        value: period.id,
        label: `${t("captureGrades:main.period")} ${index + 1}`,
        partials: formatPartials(period?.partials || []),
        index,
      };
    });
  };

  /**
   * Description: Function to format partials
   * @param {Array} [partials=[]]
   * @return {Array}
   */
  const formatPartials = (partials = []) => {
    return partials.map((partial, partialPos) => {
      return {
        id: partial,
        value: partial,
        label: `${t("captureGrades:main.partial")} ${partialPos + 1}`,
        position: partialPos,
      };
    });
  };

  /**
   * Description: Function to format assessments
   * @param {Array} [assessments=[]]
   * @return {Array}
   */
  const formatAssessments = (assessments = []) => {
    return assessments.map((assessment, index) => {
      const isOnlyOne = assessments.length === 1;
      const indexLabel = ` ${index + 1}`;
      const position = isOnlyOne ? "" : indexLabel;
      return {
        ...assessment,
        value: assessment.id,
        label: `${t("captureGrades:main.finalExam")}${position}`,
      };
    });
  };

  /**
   * Description: Function to set preselected filters for get student grades
   * @param {Array} [periods=[]]
   * @param {Array} [assessments=[]]
   */
  const setPreselectedFilters = (periods = [], assessments = []) => {
    const filterGrades = JSON.parse(localStorage.getItem(filtersKey));
    let periodPreSelected = periodSelected;
    let partialPreSelected = partialSelected;
    let finalExamPreSelected = finalExamSelected;

    // Set default period and partial or assessment
    if (periods.length > 0 && !periodSelected?.id) {
      if (filterGrades?.finalExamId) {
        const finalExamFound =
          filterGrades?.finalExamId &&
          assessments.find(
            (assessment) => assessment.id == filterGrades.finalExamId,
          );
        if (finalExamFound) {
          finalExamPreSelected = finalExamFound;
          setFinalExamSelected(finalExamFound);
        }
      } else {
        const periodFound =
          filterGrades?.periodId &&
          periods.find((period) => period.id == filterGrades.periodId);
        const firstPeriod = periods[0];
        periodPreSelected = periodFound || firstPeriod;
        setPeriodSelected(periodPreSelected);
        if (periodPreSelected.partials?.length > 0) {
          const partialFound =
            filterGrades?.partial &&
            periodPreSelected.partials.find(
              (partial) => partial.id == filterGrades.partial,
            );
          const firstPartial = periodPreSelected.partials[0];
          partialPreSelected = partialFound || firstPartial;
          setPartialSelected(partialPreSelected);
        }
      }
    }
    // Get grades for students
    const filters = {
      periodId: periodPreSelected?.value,
      partialId: partialPreSelected?.value,
      assessmentId: finalExamPreSelected?.value,
    };
    // Get grades for students
    getGradesForStudents(filters);
  };

  const getGradesForStudents = (filters = {}) => {
    const payload = {
      class_id: classId,
      calculate_avg: 1,
    };

    let typeScore = "";

    if (filters.periodId) {
      payload.grading_period_id = filters.periodId;
      typeScore = "period";
    }
    if (filters.partialId) {
      payload.grading_period_partial_id = filters.partialId;
      typeScore = "period_partial";
    }
    if (filters.assessmentId) {
      payload.assessment_id = filters.assessmentId;
      typeScore = "assessment";
    }

    setTypeScore(typeScore);

    setIsLoadingScores(true);

    GetStudentClass(payload)
      .then((response) => {
        if (response.status) {
          const students = response.data.map((student) => {
            return {
              ...student,
              academic_offer: student.program_name,
            };
          });
          props.setStudentsCount(students.length);
          setClassStudents(students);
          setClassStudentsBackup(students);
          formatCriterias(students);
        }
      })
      .finally(() => {
        setIsLoadingScores(false);
      });
  };

  const formatCriterias = (students = []) => {
    if (students.length) {
      let criterias = [];
      let firstMainCriteria = {};
      //let firstSecondCriteria = {};
      const periods = students[0].grading_periods;
      if (periods[0]) {
        if (periods[0]?.partials?.[0]?.criteria) {
          criterias = periods[0].partials[0].criteria;
        }
        if (periods[0]?.criteria) {
          criterias = periods[0].criteria;
        }
      }
      // Add position for each criteria
      criterias = criterias.map((mainCriteria, mainIndex) => {
        return {
          ...mainCriteria,
          index: mainIndex,
          second_criterias:
            (mainCriteria.second_criterias &&
              mainCriteria.second_criterias.map(
                (secondCriteria, secondIndex) => {
                  return {
                    ...secondCriteria,
                    index: secondIndex,
                  };
                },
              )) ||
            [],
        };
      });
      let criteriaSelected = null; //!!Object.keys(this.state.criteriaTab).length;
      //let secondCriteriaSelected = null; //!!Object.keys(this.state.secondCriteria).length;

      if (criterias.length) {
        firstMainCriteria = criteriaSelected ? criteriaTab : criterias[0] || {};
        // firstSecondCriteria =
        //   (firstMainCriteria.second_criterias &&
        //     firstMainCriteria.second_criterias[0]) ||
        //   {};

        // firstSecondCriteria =
        //   firstMainCriteria.second_criterias && secondCriteriaSelected
        //     ? this.state.secondCriteria
        //     : firstSecondCriteria;
        // si exite firstCriteriaId en filter
        // let filter = JSON.parse(localStorage.getItem(filtersKey));
        // if (filter && filter.firstCriteriaId) {
        //   const firstCriteriaPreselected = criterias.find(
        //     (criteria) =>
        //       criteria.grading_criterion_class_id == filter.firstCriteriaId,
        //   );
        //   if (firstCriteriaPreselected) {
        //     firstMainCriteria = firstCriteriaPreselected;
        //     firstSecondCriteria = {};
        //     if (
        //       firstCriteriaPreselected.second_criterias &&
        //       firstCriteriaPreselected.second_criterias.length
        //     ) {
        //       if (filter.secondCriteriaID) {
        //         const secondCriteriaPreselected =
        //           firstCriteriaPreselected.second_criterias.find(
        //             (criteria) =>
        //               criteria.grading_criterion_class_id ==
        //               filter.secondCriteriaID,
        //           );
        //         firstSecondCriteria = secondCriteriaPreselected;
        //       } else {
        //         firstSecondCriteria =
        //           firstCriteriaPreselected.second_criterias[0];
        //       }
        //     }
        //   }
        // }
        const typeScore = periods[0]?.partials?.[0]
          ? "period_partial_criterion"
          : "period_criterion";
        setTypeScore(typeScore);
      }
      // Remove filters from localStorage
      localStorage.removeItem(filtersKey);
      // this.setState({
      //   criteriaTab: firstMainCriteria,
      //   secondCriteria: firstSecondCriteria,
      //   criteriaClass: criterias,
      // });
      setCriteriaClass(criterias);
      setCriteriaTab(firstMainCriteria);
    }
  };

  /**
   * Description: Function to validate score and then create or update score
   * @param {object} event
   * @param {int} studentClassId
   * @param {int} periodId
   * @param {object} scorePrevius
   */
  const handleFocus = (
    event,
    studentClassId,
    periodId,
    scorePrevius,
    studentId,
    criteria,
  ) => {
    if (event.charCode == undefined || event.charCode == 13) {
      //set score from system numeric or customized
      const score = event.target ? event.target.value : event;
      //object with data for create/update score
      const payload = {
        type: typeScore,
        calculate_avg: true,
        grading_period_id: periodId,
        students_score: [
          {
            student_class_id: studentClassId,
            student_id: studentId,
            grading_period_id: periodId,
            scores: [{}],
          },
        ],
      };
      // Add partial
      if (typeScore != "period" && partialSelected?.id) {
        payload.students_score[0].grading_period_partial_id =
          partialSelected.id;
        payload.grading_period_partial_id = partialSelected.id;
      }
      // Add grading_criterion_class_id
      if (
        criteria &&
        (typeScore == "period_criterion" ||
          typeScore == "period_partial_criterion")
      ) {
        //const { criteriaTab, secondCriteria } = this.state;
        payload.students_score[0].scores[0].grading_criterion_class_id =
          // secondCriteria.grading_criterion_class_id || criteriaTab.grading_criterion_class_id;
          criteria.grading_criterion_class_id;
        if (criteria.grading_criterion_score_id) {
          payload.students_score[0].scores[0].grading_criterion_score_id =
            criteria.grading_criterion_score_id;
        }
      }
      // Add score
      if (score) {
        payload.students_score[0].scores[0].score = score;
      }
      // Add score id
      if (scorePrevius && scorePrevius.score_id) {
        payload.students_score[0].scores[0].score_id = scorePrevius.score_id;
      }
      // Save score for final exam
      if (typeScore === "assessment") {
        delete payload.students_score[0].scores;
        payload.students_score[0].assessment_id = scorePrevius.assessment_id;
        payload.students_score[0].score = score;
      }
      // If has score id then update else create new score
      createStudentScore(payload, studentId);
    }
  };

  /**
   * Description: Function to change score from period selected of the student
   * @param {object} event
   * @param {string} studentId
   * @param {integer} periodIndex
   */
  const changeScorePeriod = (event, studentId, periodIndex, criteriaPos) => {
    const classStudentsCopy = [...classStudents];
    let value = event.target.value;
    const studentIndex = classStudentsCopy.findIndex(
      (student) => student.student_id === studentId,
    );
    // Validate if the student was found
    if (studentIndex !== -1) {
      if (scoreConfig.score_system === "customized") {
        const regexLetter = /[A-Za-z ñ]/;
        if (regexLetter.test(value) || value === "") {
          classStudentsCopy[studentIndex].scores_periods[periodIndex].score =
            value.toUpperCase();
        }
      } else {
        const regexInteger = /^([0-9]|[1-9][0-9]|100)$/;
        const regexDecimals =
          scoreConfig.grading_precision == "1"
            ? /^(\d{0,2}(\.\d{0,1})?|100(\.00?)?)$/
            : /^(\d{0,2}(\.\d{0,2})?|100(\.00?)?)$/;
        const regexNumeric =
          scoreConfig.grading_period_number_format === "integer"
            ? regexInteger
            : regexDecimals;
        if (regexNumeric.test(value) || value === "") {
          //const { typeScore, criteriaTab, secondCriteria } = this.state;
          const student = classStudentsCopy[studentIndex];
          const { grading_periods, assessments } = student;
          const period = grading_periods[0];
          const assessment = assessments[0];
          const partial = period && period.partials && period.partials[0];
          //const criteriaPos = criteriaTab.index;
          //const secondCriteriaPos = secondCriteria.index;
          const targets = {
            period_partial_criterion:
              partial && partial.criteria && partial.criteria[criteriaPos],
            // secondCriteria.name
            //   ? partial &&
            //     partial.criteria[criteriaPos].second_criterias[
            //       secondCriteriaPos
            //     ]
            //   : partial && partial.criteria && partial.criteria[criteriaPos],
            period_criterion:
              period && period.criteria && period.criteria[criteriaPos],
            // secondCriteria.name
            //   ? period.criteria &&
            //     period.criteria[criteriaPos].second_criterias[secondCriteriaPos]
            //   : period.criteria && period.criteria[criteriaPos],
            period_partial: partial,
            assessment,
            period,
          };
          targets[typeScore].score = value;
          classStudentsCopy[studentIndex] = student;
        }
      }
    }
    setClassStudents(classStudentsCopy);
  };

  const createStudentScore = (student_score, studentId) => {
    SaveStudentsScore(student_score).then((response) => {
      if (!response.status) return;
      const data = response.data;
      // CM-53459: using the functional form avoids the stale closure snapshot.
      // An observation typed/pasted while this POST was in flight must survive
      // the score response.
      setClassStudents((prev) => {
        const classStudentsCopy = [...prev];
        const position = classStudentsCopy.findIndex(
          (student) => student.student_id === studentId,
        );
        if (position === -1) return prev;
        // TODO: No response from API
        if (student_score.type === "assessment") {
          const newScore = student_score.students_score[0].score;
          const assessmentPos = classStudentsCopy[
            position
          ].assessments.findIndex(
            (assessment) =>
              assessment.assessment_id ===
              student_score.students_score[0].assessment_id,
          );
          if (assessmentPos !== -1) {
            classStudentsCopy[position].assessments[assessmentPos].score =
              newScore;
          }
          return classStudentsCopy;
        }
        // Validate response
        if (data[0].length === 0) return prev;
        // The new rating is captured
        const newScore = { ...data[0].grading_periods[0] };
        // CM-53459: score and comment share grading_periods[0]; preserve the
        // on-screen comment (possibly being edited) so the score response does
        // not wipe it out.
        const currentPeriod =
          classStudentsCopy[position].grading_periods[0] || {};
        newScore.comment = currentPeriod.comment;
        newScore.comment_id = currentPeriod.comment_id;
        classStudentsCopy[position].grading_periods[0] = newScore;
        classStudentsCopy[position].average = data[0].average;
        return classStudentsCopy;
      });
    });
  };

  /**
   *Purpose: save comments or delete in case an empty string is entered
   * @param {object} event
   * @param {Int} studentId
   */
  const changeComments = (event, studentId) => {
    const classStudentsCopy = [...classStudents];
    const value = event.target.value;
    const indexStudent = classStudentsCopy.findIndex(
      (student) => student.student_id === studentId,
    );
    classStudentsCopy[indexStudent].grading_periods[0].comment = value;
    setClassStudents(classStudentsCopy);
  };

  /**
   * purpose: Save comments
   * @param {Int} studentClassId
   * @param {Int} periodId
   * @param {string} comment
   */
  const saveCommentsStudents = (studentClassId, periodId, comment) => {
    const request = {
      student_class_id: studentClassId,
      grading_period_id: periodId,
      comment: comment,
    };
    CreateStudentComment(request).then((response) => {
      if (!response.status) return;
      const data = response.data;
      // CM-53459: functional form + merge only the comment fields, without
      // touching the score, so a score save resolving later is not overwritten.
      setClassStudents((prev) => {
        const classStudentsCopy = [...prev];
        const position = classStudentsCopy.findIndex(
          (student) => student.student_class_id == studentClassId,
        );
        if (position === -1) return prev;
        classStudentsCopy[position].grading_periods[0].comment_id = data.id;
        classStudentsCopy[position].grading_periods[0].comment = data.comment;
        return classStudentsCopy;
      });
    });
  };

  const goToEvaluationCriteria = () => {
    const periodId = periodSelected?.value;
    const partial = partialSelected?.value;
    // TODO: Habilitar criterios de evaluación
    // const firstCriteriaId =
    //   this.state.criteriaTab &&
    //   this.state.criteriaTab.grading_criterion_class_id;
    // const secondCriteriaID =
    //   this.state.secondCriteria &&
    //   this.state.secondCriteria.grading_criterion_class_id;
    // save values in localStorage
    const filtersclassRating = {
      classId,
      periodId,
      partial,
    };
    // if (firstCriteriaId) filtersclassRating.firstCriteriaId = firstCriteriaId;
    // if (secondCriteriaID)
    //   filtersclassRating.secondCriteriaID = secondCriteriaID;
    localStorage.setItem(filtersKey, JSON.stringify(filtersclassRating));
    let path =
      "/capture_grades?" +
      "origin=classProfile" +
      "&classId=" +
      classId +
      "&periodId=" +
      periodId +
      "&partialId=" +
      partial +
      "&addCriterias=true";
    // if (firstCriteriaId) path += "&firstCriteriaId=" + firstCriteriaId;
    // if (secondCriteriaID) path += "&secondCriteriaID=" + secondCriteriaID;
    // TODO: Verificar que la navegación funcione correctamente
    history.push(path);
  };

  /**
   * Description: Function to handle save score format from modal
   * @param {object} scoreConfig
   */
  const onSaveScoreFormat = (scoreConfig) => {
    const filters = {
      periodId: periodSelected?.value,
      partialId: partialSelected?.value,
      assessmentId: finalExamSelected?.value,
    };
    setScoreConfig(scoreConfig);
    setShowModalScoreFormat(false);
    getGradesForStudents(filters);
  };

  /**
   * Description: Function to get file name for download class scores
   * @return {string}
   */
  const getFileName = () => {
    const grades = t("class:classGrades.grades");
    const template = t("global:importCSV.template");
    const {
      classTitle,
      schoolCycle,
      schoolLevel,
      programLevel,
      gradeLevel,
      gradeGroup,
    } = props.classInfo;

    const cycle = schoolCycle?.label;
    const level = schoolLevel?.label;
    const gradeGroupLevel = programLevel?.label
      ? programLevel.label
      : gradeLevel?.label + gradeGroup?.label;

    return `${grades}-${classTitle}-${cycle}-${level}-${gradeGroupLevel}-${template}`;
  };

  /**
   * Description: Function to download class scores in format CSV
   * @param {boolean} [onlyPeriodSelected=false]
   */
  const downloadScoresTemplatePDF = (onlyPeriodSelected = false) => {
    const payload = {
      class_id: classId,
    };
    if (onlyPeriodSelected) {
      payload.grading_period_id = periodSelected?.value;
    }
    setIsDownloadingScores(true);
    GetClassScorePDF(payload)
      .then((response) => {
        if (response.status === 200) {
          const fileName = getFileName();
          fileDownload(response.data, `${fileName}.pdf`, "application/pdf");
        }
      })
      .finally(() => {
        setIsDownloadingScores(false);
      });
  };

  /**
   * Description: Function to download class scores in format CSV
   * @param {boolean} [onlyPeriodSelected=false]
   */
  const downloadScoresTemplateCsv = (onlyPeriodSelected = false) => {
    const payload = {
      class_id: classId,
    };
    if (onlyPeriodSelected) {
      payload.grading_period_id = periodSelected?.value;
    }
    setIsDownloadingScores(true);
    GetClassScoreCSV(payload)
      .then((response) => {
        if (response.status === 200) {
          const fileName = getFileName();
          fileDownload(response.data, `${fileName}.csv`, "text/csv");
        }
      })
      .finally(() => {
        setIsDownloadingScores(false);
      });
  };

  /**
   * Description: Function to filter students locally
   * @param {string} [value=""]
   */
  const localStudentFilter = (value = "") => {
    const parseValue = value ? value.toLowerCase().trim() : "";

    const filteredStudents = classStudentsBackup.filter((student) => {
      const parseStudent = student.student_name.toLowerCase().trim();
      return parseStudent.includes(parseValue);
    });

    setClassStudents(filteredStudents);
  };

  const addNewStudent = () => {
    const students = classStudentsBackup.map((student) => ({
      id: student.student_id,
      name: student.student_name,
    }));
    classId == "new"
      ? props.setFieldStudents(students)
      : props.setCurrentStudents(students);
    props.setShowModalAddStudents(true);
  };

  const removeStudent = () => {
    const payload = {
      student_id: studentToDelete,
      class_id: classId,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    DeleteStudentClass(payload).then(() => {
      getPeriodsForClass();
    });
  };

  const importScoresButton = () => {
    const hasCriterias = criteriaClass.length > 0;
    const hasPartials = periods.some((period) => period.partials?.length > 0);
    const canImportScores = !hasCriterias && !hasPartials;

    const importBtn = (
      <Button
        variant="outline-secondary"
        className="me-3 d-inline-block"
        onClick={() => {
          setShowModalImportScores(true);
        }}
        disabled={!canImportScores}
      >
        {t("global:buttons.import")}
      </Button>
    );

    if (canImportScores) return importBtn;

    return (
      <OverlayTrigger
        placement="right"
        overlay={
          <Tooltip id="importScoresTooltip">
            {t("class:classGrades.importScoresTooltip")}
          </Tooltip>
        }
      >
        {importBtn}
      </OverlayTrigger>
    );
  };

  const lazyLoading = (rows = 1) => {
    return (
      <React.Fragment>
        {Array.from({ length: rows }).map((_, index) => (
          <Row key={index} className="table-lazyLoading mt-2">
            <Col md={12}>
              <div />
            </Col>
          </Row>
        ))}
      </React.Fragment>
    );
  };

  const toolBar = () => (
    <Row className="align-items-center">
      {/* Search student */}
      <Col xs={12} xxl={3}>
        <Form.Control
          type="text"
          placeholder={t("class:attendance.searchStudent")}
          name="searchStudent"
          id="searchStudent"
          onChange={(e) => {
            localStudentFilter(e.target.value);
          }}
        />
      </Col>
      <Col
        xs={12}
        xxl={9}
        className="d-flex justify-content-end align-items-center"
      >
        {/* Periods dropdown */}
        <FormGroup className="me-3">
          <Dropdown>
            <Dropdown.Toggle
              className="text-truncate"
              id="periods"
              variant="outline-secondary"
              //disabled={!classSelected}
            >
              {periodSelected?.label ||
                finalExamSelected?.label ||
                t("captureGrades:main.period")}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {periods.map((period) => {
                return (
                  <Dropdown.Item
                    key={period.value}
                    onClick={() => {
                      const filters = {};
                      const firstPartial = period.partials?.[0] || null;
                      filters.periodId = period.value;
                      filters.partialId = firstPartial?.value;
                      setPeriodSelected(period);
                      setPartialSelected(firstPartial);
                      setFinalExamSelected(null);
                      getGradesForStudents(filters);
                    }}
                  >
                    {period.label}
                  </Dropdown.Item>
                );
              })}
              {assessments?.map((assessment) => {
                return (
                  <Dropdown.Item
                    key={assessment.id}
                    onClick={() => {
                      const filters = { assessmentId: assessment.value };
                      setPeriodSelected(null);
                      setPartialSelected(null);
                      setFinalExamSelected(assessment);
                      getGradesForStudents(filters);
                    }}
                  >
                    {assessment.label}
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
        </FormGroup>
        {/* Partials dropdown */}
        {!finalExamSelected && (
          <FormGroup className="me-3">
            <Dropdown>
              <Dropdown.Toggle
                id="partials"
                variant="outline-secondary"
                className="text-truncate"
                disabled={!periodSelected?.partials?.length}
              >
                {partialSelected?.label || t("captureGrades:main.partial")}
              </Dropdown.Toggle>

              <Dropdown.Menu>
                {periodSelected?.partials?.map((partial) => {
                  return (
                    <Dropdown.Item
                      key={partial.value}
                      onClick={() => {
                        const filters = {
                          periodId: periodSelected.value,
                          partialId: partial.value,
                        };
                        setPartialSelected(partial);
                        getGradesForStudents(filters);
                      }}
                    >
                      {partial.label}
                    </Dropdown.Item>
                  );
                })}
              </Dropdown.Menu>
            </Dropdown>
          </FormGroup>
        )}

        {permissions.classes.edit && (
          <React.Fragment>
            {/* Import button */}
            {importScoresButton()}
            {/* Download dropdown */}
            <FormGroup className="me-3">
              <Dropdown>
                <Dropdown.Toggle
                  id="downloadClassScoresDropdown"
                  className="text-truncate"
                  variant="outline-secondary"
                  disabled={isDownloadingScores}
                >
                  {t("global:buttons.download")}
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{
                    width: "210px",
                    top: "100%",
                  }}
                >
                  <Dropdown.Item
                    id="selectedPeriod"
                    className="custom-menu-item"
                    as="div"
                  >
                    {t("class:classGrades.downloadSelectedPeriod")}
                    <Dropdown.Menu className="dropdown-submenu">
                      <Dropdown.Item
                        id="selectedPeriodPDF"
                        className="custom-submenu-item"
                        as="button"
                        type="button"
                        onClick={() => downloadScoresTemplatePDF(true)}
                      >
                        {t("global:buttons.downloadPDF")}
                      </Dropdown.Item>
                      <Dropdown.Item
                        id="selectedPeriodCSV"
                        className="custom-submenu-item"
                        as="button"
                        type="button"
                        onClick={() => downloadScoresTemplateCsv(true)}
                      >
                        {t("global:buttons.downloadExcel")}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown.Item>
                  <Dropdown.Item
                    id="allPeriods"
                    className="custom-menu-item"
                    as="div"
                  >
                    {t("class:classGrades.downloadAllPeriods")}
                    <Dropdown.Menu
                      className="dropdown-submenu"
                      style={{ marginTop: "30px" }}
                    >
                      <Dropdown.Item
                        id="allPeriodsPDF"
                        className="custom-submenu-item"
                        as="button"
                        type="button"
                        onClick={() => downloadScoresTemplatePDF(false)}
                      >
                        {t("global:buttons.downloadPDF")}
                      </Dropdown.Item>
                      <Dropdown.Item
                        id="allPeriodsCSV"
                        className="custom-submenu-item"
                        as="button"
                        type="button"
                        onClick={() => downloadScoresTemplateCsv(false)}
                      >
                        {t("global:buttons.downloadExcel")}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </FormGroup>
          </React.Fragment>
        )}

        {/* Switch send review */}
        <Form className="me-3">
          <Form.Label className="me-1 d-inline">
            {t("class:classGrades.sendForReview")}
          </Form.Label>
          <Form.Check
            id="sendReviewSwitch"
            className="d-inline-block"
            type="switch"
            defaultChecked={false}
            //checked={feature.isAvailable}
            //disabled={isSaving}
            // onChange={(e) =>
            //   activateFeature({
            //     ...feature,
            //     is_active: e.target.checked ? 1 : 0,
            //   })
            // }
          />
        </Form>
        {/* Switch send review when is clicked show dropdown*/}
        <Dropdown>
          <Dropdown.Toggle
            className="text-truncate"
            id="classSettingsDropdown"
            variant="outline-secondary"
          >
            <i role="button" className="bi bi-gear fa-lg" />
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item
              id="openModalScoreFormat"
              onClick={() => setShowModalScoreFormat(true)}
            >
              {t("class:modalScoreFormat.title")}
            </Dropdown.Item>
            {permissions.evaluation_criteria.create && (
              <Dropdown.Item
                id="goToEvaluationCriteria"
                onClick={() =>
                  criteriaClass.length > 0 && goToEvaluationCriteria()
                }
                style={
                  criteriaClass.length > 0
                    ? {}
                    : {
                        opacity: "0.5",
                        cursor: "not-allowed",
                      }
                }
              >
                {t("class:modalGrandingCriteria.title")}
              </Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </Col>
    </Row>
  );

  const addNewStudentBtn = (
    <i
      id="addStudent"
      className="bi bi-plus-circle-fill float-end"
      style={customStyles.iconStudent}
      onClick={() => addNewStudent()}
    />
  );

  const iconRemoveStudent = (id, index) => (
    <i
      className="bi bi-x-circle-fill float-end"
      style={customStyles.iconRemoveX}
      id={"removeStudent-" + index}
      role="button"
      onClick={() => {
        setShowModalDelete(true);
        setStudentToDelete(id);
      }}
    />
  );

  const studentsScoresAvg = () => (
    <Table responsive bordered>
      <thead>
        <tr>
          <th style={{ width: " 250px" }} className="class-static-cell">
            <div className="class-static-cell">
              <Col
                md={validateUserRoleIds([1, 2, 16], null, "&&", true) ? 12 : 11}
                className="no-padding-sides class-static-cell"
              >
                {t("class:classGrades.studentName")}
              </Col>
              <Col
                md={1}
                className={
                  validateUserRoleIds([1, 2, 16], null, "&&", true)
                    ? "hide"
                    : "no-padding-sides class-static-cell"
                }
              >
                {permissions.classes.create && addNewStudentBtn}
              </Col>
            </div>
          </th>
          {classId != "new" &&
            periods.map(function (period) {
              return (
                <th
                  key={period.value}
                  style={{
                    textAlign: "center",
                  }}
                >
                  {period.label}
                </th>
              );
            })}

          {classId != "new" && (
            <th style={{ width: " 100px" }} className="class-static-cell">
              <div>{t("class:classGrades.avgGeneral")}</div>
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {classStudents.map(function (student, index) {
          return (
            <tr key={index}>
              <td
                className="class-static-cell align-content-center"
                style={{ zIndex: 1 }}
              >
                <div className="d-flex justify-content-between">
                  {validateUserRoleIds([1, 2, 16], null, "&&", true) ? (
                    <a
                      href={`/estudiante/${student.id}`}
                      style={{
                        cursor: "pointer",
                      }}
                    >
                      {student.student_name +
                        " - " +
                        (student.academic_offer != null
                          ? "Nivel Superior"
                          : `${student.grade}${student.group}`)}
                    </a>
                  ) : (
                    student.student_name +
                    " - " +
                    (student.academic_offer != null
                      ? "Nivel Superior"
                      : `${student.grade}${student.group}`)
                  )}
                  {permissions.classes.delete &&
                    iconRemoveStudent(student.student_id, index)}
                </div>
              </td>
              {/* only available when editing a class */}
              {classId != "new" &&
                periods.map(function (period, p_index) {
                  return (
                    <td
                      key={p_index}
                      className="align-content-center"
                      id="Per-4"
                      style={{
                        textAlign: "center",
                      }}
                    >
                      <p>
                        {student.scores_periods &&
                        student.scores_periods[p_index]
                          ? student.scores_periods[p_index].score
                          : ""}
                      </p>
                    </td>
                  );
                })}
              {classId != "new" && (
                <td className="align-content-center">
                  {student.average
                    ? student === "numeric"
                      ? student.average
                      : student.average + "%"
                    : ""}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </Table>
  );

  const studentsScoresV2 = () => {
    const criteriasCount = finalExamSelected ? 0 : criteriaClass?.length;
    let columnFocus = "";
    if (partialSelected?.id) {
      let partialIndex = 0;
      for (const item of classStudents) {
        const periods = item.grading_periods || [];
        // Buscar su periodo seleccionado
        const period = periods.find(
          (score) => score.grading_period_id == periodSelected.id,
        );
        // Buscar su parcial seleccionado
        partialIndex = period?.partials?.findIndex(
          (score) => score.grading_period_partial_id == partialSelected.id,
        );
        item.score = period?.partials?.[partialIndex] || "";
      }
      columnFocus = partialSelected.label;
    } else if (periodSelected?.id) {
      let periodIndex = 0;
      for (const item of classStudents) {
        const periods = item.grading_periods || [];
        periodIndex = periods.findIndex(
          (score) => score.grading_period_id == periodSelected.id,
        );
        item.score = periods[periodIndex];
      }
      columnFocus = periodSelected.label;
    } else if (finalExamSelected?.id) {
      for (const item of classStudents) {
        let assessmentIndex = 0;
        assessmentIndex = item.assessments.findIndex(
          (score) => score.assessment_id == finalExamSelected.id,
        );
        item.score = item.assessments[assessmentIndex];
      }
      columnFocus = finalExamSelected.label;
    }
    return (
      <Table responsive bordered id="tableStudentScoresV2">
        <thead style={{ position: "sticky" }} className="class-static-cell">
          <tr>
            <th width={criteriasCount ? "20%" : "35%"}>
              {t("class:classGrades.studentName")}
              {permissions.classes.create && addNewStudentBtn}
            </th>
            {criteriasCount > 0 ? (
              criteriaClass.map((criteria) => {
                return (
                  <th
                    key={criteria.index}
                    width={60 / criteriasCount + "%"}
                    style={{ textAlign: "center" }}
                  >
                    <div
                      style={{
                        display: "inline-flex",
                        gap: "10px",
                        flexWrap: "wrap-reverse",
                      }}
                    >
                      {/** Score concept name  */}
                      {criteria.name}
                      {/** Score percentage only for criterias */}
                      {criteria.percentage > 0 && criteria.evaluable ? (
                        <span style={{ color: "#ea2c54" }}>
                          {` ${criteria.percentage}%`}
                        </span>
                      ) : (
                        <span>
                          {/** Tooltip only for criterias no evaluables */}
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="criteriaNoEvaluable">
                                {t("class:classGrades.criteriaNoEvaluable")}
                              </Tooltip>
                            }
                          >
                            <i
                              className="fas fa-info-circle"
                              style={{ color: "#ea2c54" }}
                            />
                          </OverlayTrigger>
                        </span>
                      )}
                    </div>
                  </th>
                );
              })
            ) : (
              <th width="30%" style={{ textAlign: "center" }}>
                <div
                  style={{
                    display: "inline-flex",
                    gap: "10px",
                    flexWrap: "wrap-reverse",
                  }}
                >
                  {/** Score concept name  */}
                  {columnFocus}
                </div>
              </th>
            )}
            {!finalExamSelected && (
              <th width={"35%"}>{t("class:classGrades.observations")}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {classStudents.map(function (student, studentIndex) {
            const studentScore = student.score
              ? student.score.criteria
                ? student.score.criteria
                : student.score
              : {};
            return (
              <tr key={student.student_id}>
                <td
                  className="class-static-cell align-content-center"
                  style={{ zIndex: 1 }}
                >
                  <div className="d-flex justify-content-between">
                    {validateUserRoleIds([1, 2, 16], null, "&&", true) ? (
                      <a
                        href={`/estudiante/${student.student_id}`}
                        style={{
                          cursor: "pointer",
                        }}
                      >
                        {/* Validacion para soportar nivel basico y superior */}
                        {student.student_name +
                          " - (" +
                          (student.grade != null
                            ? student.grade
                            : "Nivel Superior") +
                          (student.group != null ? student.group : "") +
                          ")"}
                      </a>
                    ) : (
                      /* Validacion para soportar nivel basico y superior */
                      student.student_name +
                      " - (" +
                      (student.grade != null
                        ? student.grade
                        : "Nivel Superior") +
                      (student.group != null ? student.group : "") +
                      ")"
                    )}
                    {permissions.classes.delete &&
                      iconRemoveStudent(student.student_id, studentIndex)}
                  </div>
                </td>
                {/* Score */}
                {criteriasCount > 0 ? (
                  criteriaClass.map((criteria) => {
                    const studentScoreCriteria = studentScore[criteria.index];

                    return (
                      <td
                        key={criteria.index}
                        className="align-content-center"
                        width={60 / criteriasCount + "%"}
                        style={{
                          textAlignLast: "center",
                          justifyItems: "center",
                        }}
                      >
                        {scoreConfig.score_system === "customized" ? (
                          <Dropdown
                            id="scoreCustomized"
                            onSelect={(value) => {
                              handleFocus(
                                value,
                                student.student_class_id,
                                periodSelected.id,
                                studentScore,
                                student.student_id,
                                studentScoreCriteria,
                              );
                            }}
                          >
                            <Dropdown.Toggle variant="outline-secondary">
                              {studentScoreCriteria?.score}
                            </Dropdown.Toggle>
                            <Dropdown.Menu style={{ minWidth: "70px" }}>
                              {/**for remove score from a period */}
                              <Dropdown.Item
                                className="fas"
                                id={`removeCustomScore_criteria${criteria.index}_student${studentIndex}`}
                                eventKey={""}
                              >
                                X
                              </Dropdown.Item>
                              {/**List with the equivalencences */}
                              {scoreConfig.equivalences.map(
                                function (equivalence, index) {
                                  return (
                                    <Dropdown.Item
                                      key={index}
                                      id={`customScore${index}_criteria${criteria.index}_student${studentIndex}`}
                                      eventKey={equivalence.score_letter}
                                    >
                                      {equivalence.score_letter}
                                    </Dropdown.Item>
                                  );
                                },
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        ) : (
                          <Form.Control
                            style={{ width: "78px", height: "28px" }}
                            id={`score_criteria${criteria.index}_student${studentIndex}`}
                            className={"class-score-input"}
                            type="text"
                            onBlur={(e) =>
                              handleFocus(
                                e,
                                student.student_class_id,
                                periodSelected.id,
                                studentScore,
                                student.student_id,
                                studentScoreCriteria,
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                const inputSelected = document.getElementById(
                                  `score_criteria${criteria.index}_student${studentIndex}`,
                                );
                                inputSelected.blur();
                              }
                            }}
                            value={
                              studentScoreCriteria
                                ? studentScoreCriteria.score
                                : ""
                            }
                            onChange={(e) =>
                              changeScorePeriod(
                                e,
                                student.student_id,
                                periodSelected?.position,
                                criteria.index,
                              )
                            }
                          />
                        )}
                      </td>
                    );
                  })
                ) : (
                  <td
                    id="Per-4"
                    style={{
                      textAlignLast: "center",
                      justifyItems: "center",
                    }}
                    className="align-content-center"
                  >
                    {scoreConfig.score_system === "customized" ? (
                      <Dropdown
                        id="scoreCustomized"
                        onSelect={(value) => {
                          handleFocus(
                            value,
                            student.student_class_id,
                            periodSelected?.id,
                            studentScore,
                            student.student_id,
                          );
                        }}
                      >
                        <Dropdown.Toggle variant="outline-secondary">
                          {studentScore.score || ""}
                        </Dropdown.Toggle>
                        <Dropdown.Menu style={{ minWidth: "70px" }}>
                          {/**for remove score from a period */}
                          <Dropdown.Item
                            className="fas"
                            eventKey={""}
                            id={`removeCustomScore_student${studentIndex}`}
                          >
                            X
                          </Dropdown.Item>
                          {/**List with the equivalencences */}
                          {scoreConfig.equivalences.map(
                            function (equivalence, index) {
                              return (
                                <Dropdown.Item
                                  key={index}
                                  id={`customScore${index}_student${studentIndex}`}
                                  eventKey={equivalence.score_letter}
                                >
                                  {equivalence.score_letter}
                                </Dropdown.Item>
                              );
                            },
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    ) : (
                      <Form.Control
                        style={{ width: "78px", height: "28px" }}
                        id={`score_student${studentIndex}`}
                        className={"class-score-input"}
                        type="text"
                        onBlur={(e) =>
                          handleFocus(
                            e,
                            student.student_class_id,
                            periodSelected?.id,
                            studentScore,
                            student.student_id,
                          )
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            const inputSelected = document.getElementById(
                              `score_student${studentIndex}`,
                            );
                            inputSelected.blur();
                          }
                        }}
                        value={studentScore.score || ""}
                        onChange={(e) =>
                          changeScorePeriod(
                            e,
                            student.student_id,
                            periodSelected?.position,
                          )
                        }
                      />
                    )}
                  </td>
                )}
                {/* Observation */}
                {!finalExamSelected && (
                  <td
                    id="Per-4"
                    style={{
                      textAlign: "center",
                    }}
                    className="align-content-center"
                  >
                    <Form.Control
                      as="textarea"
                      id={"comment_student" + studentIndex}
                      rows="2"
                      cols="150"
                      maxLength={periods.length > 10 ? "150" : "250"}
                      style={{
                        resize: "none",
                        maxWidth: "500px",
                      }}
                      value={
                        student.grading_periods &&
                        student.grading_periods[0] &&
                        student.grading_periods[0].comment != null
                          ? student.grading_periods[0].comment
                          : ""
                      }
                      onBlur={() =>
                        //save comments when defocusing
                        saveCommentsStudents(
                          student.student_class_id,
                          periodSelected.id,
                          student.grading_periods &&
                            student.grading_periods[0].comment,
                        )
                      }
                      onChange={(e) =>
                        //change the comments
                        changeComments(e, student.student_id)
                      }
                    />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  const studentsScoresOnlyPeriods = () => (
    <Table responsive bordered id="tableStudentScoresOnlyPeriods">
      <thead style={{ position: "sticky" }} className="class-static-cell">
        <tr>
          <th width="30%">
            {t("class:classGrades.studentName")}
            {permissions.classes.create && addNewStudentBtn}
          </th>
          <th width="10%"> {t("class:classGrades.grades")} </th>
          <th width="60%">
            {t("class:classGrades.observations")}
            <span
              style={{
                fontSize: "10px",
                paddingLeft: "10px",
                color: "red",
                fontWeight: "normal",
              }}
            >
              {t("class:classGrades.observationNote")}
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        {classStudents.map((student, index) => {
          return (
            <tr key={index}>
              <td width="30%" className="align-content-center">
                <div className="d-flex justify-content-between">
                  {validateUserRoleIds([1, 2, 16], null, "&&", true) ? (
                    <a
                      href={`/estudiante/${student.student_id}`}
                      style={{
                        cursor: "pointer",
                      }}
                    >
                      {/* Validacion para soportar nivel basico y superior */}
                      {student.student_name +
                        " - " +
                        (student.academic_offer != null
                          ? "Nivel Superior"
                          : `${student.grade}${student.group}`)}
                    </a>
                  ) : (
                    /* Validacion para soportar nivel basico y superior */
                    student.student_name +
                    " - " +
                    (student.academic_offer != null
                      ? "Nivel Superior"
                      : `${student.grade}${student.group}`)
                  )}
                  {permissions.classes.delete &&
                    iconRemoveStudent(student.student_id, index)}
                </div>
              </td>
              <td
                width="10%"
                className="cleanFilters align-content-center"
                style={{
                  textAlignLast: "center",
                  justifyItems: "center",
                }}
              >
                {scoreConfig.score_system === "customized" ? (
                  <Dropdown
                    id="scoreCustomized"
                    onSelect={(value) =>
                      handleFocus(
                        value,
                        student.student_class_id,
                        periodSelected.id,
                        student.grading_periods[0],
                        student.student_id,
                      )
                    }
                  >
                    <Dropdown.Toggle variant="outline-secondary">
                      {student.grading_periods && student.grading_periods[0]
                        ? student.grading_periods[0].score
                        : ""}
                    </Dropdown.Toggle>
                    <Dropdown.Menu
                      style={{
                        minWidth: "70px",
                      }}
                    >
                      {/**for remove score from a period */}
                      <Dropdown.Item className="fas" eventKey={""}>
                        X
                      </Dropdown.Item>
                      {/**List with the equivalencences */}
                      {scoreConfig.equivalences.map(
                        function (equivalence, index) {
                          return (
                            <Dropdown.Item
                              key={index}
                              eventKey={equivalence.score_letter}
                            >
                              {equivalence.score_letter}
                            </Dropdown.Item>
                          );
                        },
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                ) : (
                  <Form.Control
                    style={{ width: "78px", height: "28px" }}
                    id={"score" + index}
                    className={"class-score-input"}
                    type="text"
                    onBlur={(e) =>
                      handleFocus(
                        e,
                        student.student_class_id,
                        periodSelected.id,
                        student.grading_periods[0],
                        student.student_id,
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const inputSelected = document.getElementById(
                          "score" + index,
                        );
                        inputSelected.blur();
                      }
                    }}
                    value={
                      student.grading_periods &&
                      student.grading_periods[0] &&
                      student.grading_periods[0].score != null
                        ? student.grading_periods[0].score
                        : ""
                    }
                    onChange={(e) =>
                      changeScorePeriod(e, student.student_id, 0)
                    }
                  />
                )}
              </td>
              <td width="60%" className="align-content-center">
                <div>
                  <Form.Control
                    as="textarea"
                    id={"comment_student" + index}
                    rows="2"
                    cols="150"
                    maxLength={periods.length > 10 ? "150" : "250"}
                    style={{
                      resize: "none",
                    }}
                    value={
                      student.grading_periods &&
                      student.grading_periods[0] &&
                      student.grading_periods[0].comment != null
                        ? student.grading_periods[0].comment
                        : ""
                    }
                    onBlur={() =>
                      //save comments when defocusing
                      saveCommentsStudents(
                        student.student_class_id,
                        periodSelected.id,
                        student.grading_periods[0].comment,
                      )
                    }
                    onChange={(e) =>
                      //change the comments
                      changeComments(e, student.student_id)
                    }
                  />
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );

  const renderStudentScores = () => {
    const isFinalExamView = periodSelected?.id === null && finalExamSelected;
    const hasCriterias = criteriaClass.length > 0;
    const hasPartials = periodSelected?.partials?.length > 0;
    const isPartialsCriteriasView =
      hasCriterias || hasPartials || finalExamSelected != null;

    return isFinalExamView
      ? studentsScoresAvg()
      : isPartialsCriteriasView
        ? studentsScoresV2()
        : studentsScoresOnlyPeriods();
  };

  return (
    <React.Fragment>
      {isLoading ? (
        lazyLoading()
      ) : (
        <div className="p-2" style={{ border: "solid 1px #dee2e6 " }}>
          {toolBar()}
        </div>
      )}
      <Row
        className="mb-2"
        style={{
          maxHeight: containerHeight,
          overflowY: "auto",
        }}
      >
        <Col xs={12}>
          {isLoading || isLoadingScores
            ? lazyLoading(4)
            : renderStudentScores()}
        </Col>
      </Row>
      {/* Modals */}
      {showModalScoreFormat && (
        <ModalScoreFormat
          classId={classId}
          show={showModalScoreFormat}
          onClose={() => setShowModalScoreFormat(false)}
          scoreConfig={scoreConfig}
          setScoreConfig={setScoreConfig}
          onSave={onSaveScoreFormat}
        />
      )}
      {showModalImportScores && (
        <ModalImportScores
          show={showModalImportScores}
          onHide={() => setShowModalImportScores(false)}
          classId={classId}
          periodId={periodSelected?.id}
        />
      )}
      {showModalDelete && (
        <ModalConfirmDelete
          showModalDelete={showModalDelete}
          title={t("class:stundentsClass.deleteStudent")}
          message={t("class:stundentsClass.deleteStudentMessage")}
          fnCancelButton={() => {
            setShowModalDelete(false);
            setStudentToDelete(null);
          }}
          fnAcceptButton={() => {
            removeStudent();
            setShowModalDelete(false);
          }}
        />
      )}
    </React.Fragment>
  );
};

ClassGrades.propTypes = {
  classId: PropTypes.string.isRequired,
  setShowModalAddStudents: PropTypes.func,
  setFieldStudents: PropTypes.func,
  setStudentsCount: PropTypes.func,
  refreshStudentList: PropTypes.bool,
  setRefreshStudentList: PropTypes.func,
  classInfo: PropTypes.object,
  setCurrentStudents: PropTypes.func,
};

export default ClassGrades;
